<script setup>
import { reactive, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

const emit = defineEmits(['submit', 'cancel']);

function normalizePhone(raw) {
  if (!raw) return null;
  const stripped = raw.trim();
  const hasPlus = stripped.startsWith('+');
  const digits = stripped.replace(/\D/g, '');
  if (digits.length < 8) return null;
  if (hasPlus) return `+${digits}`;
  if (digits.startsWith('55') && digits.length >= 12 && digits.length <= 13)
    return `+${digits}`;
  if (digits.length >= 10 && digits.length <= 11) return `+55${digits}`;
  return `+${digits}`;
}

function parsePhoneText(text) {
  return text
    .split(/[\n,;]+/)
    .map(s => s.trim())
    .filter(Boolean)
    .map(normalizePhone)
    .filter(Boolean);
}

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getSMSInboxes'),
};

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudience: [],
  audienceType: 'labels',
  phoneListText: '',
  sheetId: '',
};

const state = reactive({ ...initialState });
const fileInputRef = ref(null);

const parsedPhones = computed(() => parsePhoneText(state.phoneListText));

const audienceValid = computed(() => {
  if (state.audienceType === 'phone_list') return parsedPhones.value.length > 0;
  return state.selectedAudience?.length > 0;
});

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: { required },
  selectedAudience: {},
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.SMS.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !audienceValid.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, { ...initialState });
};

const downloadTemplate = () => {
  const csv = [
    'telefone',
    '(31) 97104-5800',
    '31987654321',
    '+5521991234567',
    '5511999887766',
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'modelo_lista_numeros.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const handleFileUpload = event => {
  const file = event.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = e => {
    const text = e.target.result;
    const numbers = text
      .split(/[\n,;\r\t]+/)
      .map(s => s.replace(/[^0-9+() -]/g, '').trim())
      .filter(Boolean);
    state.phoneListText = state.phoneListText
      ? `${state.phoneListText}\n${numbers.join('\n')}`
      : numbers.join('\n');
  };
  reader.readAsText(file);
  event.target.value = '';
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => ({
  title: state.title,
  message: state.message,
  inbox_id: state.inboxId,
  scheduled_at: formatToUTCString(state.scheduledAt),
  audience:
    state.audienceType === 'phone_list'
      ? [{ type: 'PhoneList', phones: parsedPhones.value }]
      : state.selectedAudience?.map(id => ({ id, type: 'Label' })),
  additional_attributes: state.sheetId
    ? { sheet_id: state.sheetId }
    : undefined,
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.SMS.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <TextArea
      v-model="state.message"
      :label="t('CAMPAIGN.SMS.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      show-character-count
      :message="formErrors.message"
      :message-type="formErrors.message ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.SMS.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-2">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <!-- Toggle Labels / Lista de números -->
      <div class="flex gap-2">
        <button
          type="button"
          class="px-3 py-1 rounded text-xs font-medium border transition-colors"
          :class="[
            state.audienceType === 'labels'
              ? 'bg-n-brand text-white border-n-brand'
              : 'bg-n-alpha-2 text-n-slate-11 border-n-weak hover:bg-n-alpha-3',
          ]"
          @click="state.audienceType = 'labels'"
        >
          Por Labels
        </button>
        <button
          type="button"
          class="px-3 py-1 rounded text-xs font-medium border transition-colors"
          :class="[
            state.audienceType === 'phone_list'
              ? 'bg-n-brand text-white border-n-brand'
              : 'bg-n-alpha-2 text-n-slate-11 border-n-weak hover:bg-n-alpha-3',
          ]"
          @click="state.audienceType = 'phone_list'"
        >
          Lista de Números
        </button>
      </div>

      <!-- Labels -->
      <TagMultiSelectComboBox
        v-if="state.audienceType === 'labels'"
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="state.audienceType === 'labels' && !audienceValid"
        class="[&>div>button]:bg-n-alpha-black2"
      />

      <!-- Lista de números -->
      <div
        v-if="state.audienceType === 'phone_list'"
        class="flex flex-col gap-2"
      >
        <textarea
          v-model="state.phoneListText"
          rows="5"
          placeholder="Cole os números aqui, um por linha ou separados por vírgula.&#10;Ex: (31) 97104-5800&#10;31987654321&#10;+5531991234567"
          class="w-full rounded border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand resize-y"
        />
        <div class="flex items-center justify-between gap-2">
          <span class="text-xs text-n-slate-11">
            {{ parsedPhones.length }} número(s) válido(s) detectado(s)
          </span>
          <div class="flex gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded border border-n-weak bg-n-alpha-2 px-3 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-3"
              @click="downloadTemplate"
            >
              <span class="i-lucide-download w-3 h-3" />
              Baixar modelo
            </button>
            <label class="cursor-pointer">
              <input
                ref="fileInputRef"
                type="file"
                accept=".csv,.txt"
                class="hidden"
                @change="handleFileUpload"
              />
              <span
                class="inline-flex items-center gap-1 rounded border border-n-weak bg-n-alpha-2 px-3 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-3 cursor-pointer"
              >
                <span class="i-lucide-upload w-3 h-3" />
                Importar CSV
              </span>
            </label>
          </div>
        </div>
        <p
          v-if="state.audienceType === 'phone_list' && !audienceValid"
          class="text-xs text-n-red-9"
        >
          Adicione ao menos um número válido.
        </p>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        ID da Planilha Google (opcional)
      </label>
      <input
        v-model="state.sheetId"
        type="text"
        placeholder="Ex: 146YCBKl7YDdopAqcdPP4QJ7dknNAc1RJdOUbQRh4d_w"
        class="w-full rounded border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand"
      />
      <p class="mt-0.5 text-xs text-n-slate-10">
        Quando informado, as respostas dos contatos serão registradas
        automaticamente na planilha.
      </p>
    </div>

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.SMS.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.SMS.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
